import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Normalizes a site preference value returned by the fetchable-preferences
 * custom API into a strict boolean. SCAPI can hand back a real boolean, a
 * `0`/`1` number, or a `"true"`/`"false"`/`"yes"`/`"no"` string depending on how
 * the preference was set up, so any shape but an unambiguous "true" reads as
 * `false` — a feature flag must fail closed, never open, on an unexpected value.
 * @param {boolean|number|string|null|undefined} value - The raw preference value.
 * @returns {boolean}
 */
const preferenceToBool = (value) => {
    if (value === true) return true
    if (value === false || value == null) return false
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (normalized === 'true' || normalized === 'yes') return true
        if (normalized === 'false' || normalized === 'no') return false
    }
    return false
}

/**
 * Reads one or more site preferences from the storefront's own fetchable-preferences
 * custom API, using the shopper's own SLAS token.
 * @param {Object} params
 * @param {string} params.appOrigin - Origin to call the proxy through, e.g. `https://storefront.example`.
 * @param {string} params.proxyPath - The commerce API proxy path prefix.
 * @param {string} params.organizationId - The SCAPI organization id.
 * @param {string} params.siteId - The site to read preferences for.
 * @param {string} params.token - A valid shopper access token.
 * @param {string[]} params.preferenceIds - The preference ids to request.
 * @returns {Promise<Object>} The preferences response body, keyed by preference id.
 * @throws {Error} When the request does not return a 2xx response.
 */
const fetchPreferences = async ({appOrigin, proxyPath, organizationId, siteId, token, preferenceIds}) => {
    const cIds = preferenceIds.join(',')
    const url = `${appOrigin}${proxyPath}/custom/preferences/v1/organizations/${organizationId}/preferences?siteId=${siteId}&c_ids=${cIds}`

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    })

    if (!response.ok) {
        throw new Error('Failed to fetch preferences')
    }

    const data = await response.json()
    return data
}

/**
 * React Query wrapper around {@link fetchPreferences}. Client-only: the query is
 * disabled entirely during SSR (and its `isLoading` is forced to `false` there) so
 * a server render never blocks on, or attempts, this fetch — the storefront's own
 * `useAppOrigin`/`ServerContext` are not guaranteed to be available in every render
 * path (for example, in isolated component tests), so this hook derives its own
 * origin from `window.location` instead of depending on them.
 *
 * @param {string|string[]} preferenceIds - One or more preference ids to read.
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Set to `false` to skip the query entirely,
 *   for example when the feature that needs the preference isn't in play this render.
 * @param {string} [options.appOrigin] - Overrides the client-derived origin, mainly for tests.
 * @param {number} [options.staleTime=300000] - Overrides the default 5-minute staleTime.
 *   Lower this for a preference acting as a live kill switch (for example GSC's
 *   `gscEnabled<Brand>Web`), where a Business Manager change should reach an
 *   already-open tab sooner than 5 minutes. Existing callers that don't pass this
 *   keep the exact same 5-minute behavior as before.
 * @param {boolean} [options.refetchOnWindowFocus=false] - Overrides the default
 *   `false`. A kill-switch-style preference may want `true`, so tabbing back into
 *   an already-open page re-checks the value instead of waiting out the cache.
 * @param {number} [options.refetchInterval] - Not set by default (no polling, same
 *   as before this option existed). Set this for a kill-switch-style preference to
 *   bound how long an idle, already-open tab can keep acting on a stale value.
 * @returns {{data: Object, isLoading: boolean, error: Error|null}} `data` is always
 *   an object (never `undefined`), so callers can safely index into it without a null check.
 */
const useSitePreferences = (preferenceIds, options = {}) => {
    const {getTokenWhenReady} = useAccessToken()
    const {site} = useMultiSite()
    const config = getConfig()
    const {proxyPath, parameters} = config.app.commerceAPI
    const organizationId = parameters.organizationId
    const siteId = site?.id || parameters.siteId
    const onClient = typeof window !== 'undefined'
    const appOrigin = options.appOrigin || (onClient ? window.location.origin : '')
    const ids = Array.isArray(preferenceIds) ? preferenceIds : [preferenceIds]
    const key = ids.slice().sort().join(',')

    const {data, isLoading, error} = useQuery({
        queryKey: ['sitePreferences', organizationId, siteId, key],
        enabled:
            onClient &&
            options.enabled !== false &&
            !!organizationId &&
            !!siteId &&
            ids.length > 0,
        queryFn: async () => {
            const token = await getTokenWhenReady()
            return fetchPreferences({
                appOrigin,
                proxyPath,
                organizationId,
                siteId,
                token,
                preferenceIds: ids
            })
        },
        staleTime: options.staleTime ?? 5 * 60 * 1000,
        refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
        refetchInterval: options.refetchInterval
    })

    return {
        data: data || {},
        isLoading: onClient ? isLoading : false,
        error
    }
}

export {useSitePreferences, preferenceToBool}
